import { useState } from 'react'
import { getServerSessionId } from '../../lib/serverSession'
import { resolveProofUrl } from '../../lib/proofUrlResolver'

// Issue #22 Phase 2b: open a payment proof via a short-lived signed URL minted
// by the admin-gated signer, instead of linking the raw public URL directly.
// Only screenshot_url values are ever passed in (never wallet addresses).
async function openSignedProof(rawUrl: string | null | undefined) {
  const url = await resolveProofUrl(rawUrl)
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

// ============================================================================
// INTERFACES
// ============================================================================

interface MissingShareIssue {
  payment_id: string
  user_id: number
  username: string
  email: string
  amount: number
  expected_shares: number
  payment_date: string
  network: string
  currency: string
  transaction_hash: string
}

interface MissingCommissionIssue {
  share_purchase_id: string
  user_id: number
  username: string
  email: string
  sponsor_id: number
  sponsor_username: string
  sponsor_current_usdt: number
  sponsor_current_shares: number
  sponsor_total_commissions: number
  amount: number
  shares_purchased: number
  bonus_shares: number
  expected_usdt_commission: number
  expected_share_commission: number
  purchase_date: string
}

interface MissingBonusIssue {
  share_purchase_id: string
  user_id: number
  username: string
  email: string
  amount: number
  shares_purchased: number
  current_bonus_shares: number
  expected_bonus_shares: number
  purchase_date: string
}

interface DuplicateShareIssue {
  user_id: number
  username: string
  email: string
  total_payments: number
  total_payment_amount: number
  expected_shares: number
  total_share_purchases: number
  actual_shares: number
  excess_shares: number
  share_purchase_ids: string[]
}

type ActiveTab = 'balance' | 'payments' | 'commissions' | 'bonuses' | 'duplicates'

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function PaymentIntegrityAuditV2() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('balance')
  const [loading, setLoading] = useState(false)

  // Tab 1: Balance Audit (finds users with wrong total balance - DELETED shares)
  const [balanceIssues, setBalanceIssues] = useState<MissingShareIssue[]>([])
  const [balanceAuditRun, setBalanceAuditRun] = useState(false)

  // Tab 2: Payment Audit (finds specific payments missing share purchases)
  const [missingShares, setMissingShares] = useState<MissingShareIssue[]>([])
  const [sharesAuditRun, setSharesAuditRun] = useState(false)

  // Tab 3: Missing Commissions
  const [missingCommissions, setMissingCommissions] = useState<MissingCommissionIssue[]>([])
  const [commissionsAuditRun, setCommissionsAuditRun] = useState(false)

  // Tab 4: Missing Bonuses
  const [missingBonuses, setMissingBonuses] = useState<MissingBonusIssue[]>([])
  const [bonusesAuditRun, setBonusesAuditRun] = useState(false)

  // Tab 5: Duplicate Shares
  const [duplicateShares, setDuplicateShares] = useState<DuplicateShareIssue[]>([])
  const [duplicatesAuditRun, setDuplicatesAuditRun] = useState(false)
  const [reviewingUser, setReviewingUser] = useState<DuplicateShareIssue | null>(null)
  const [userSharePurchases, setUserSharePurchases] = useState<any[]>([])
  const [userPayments, setUserPayments] = useState<any[]>([])

  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [fixing, setFixing] = useState<string | null>(null)

  // ============================================================================
  // TAB 1: AUDIT BALANCE DISCREPANCIES (finds users with wrong total balance)
  // ============================================================================

  const auditBalanceDiscrepancies = async () => {
    setLoading(true)
    setMessage(null)
    setBalanceIssues([])

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'audit_balance_discrepancies', data: {} })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setBalanceIssues(result.data)
      setBalanceAuditRun(true)
      setMessage({
        type: result.data.length > 0 ? 'error' : 'success',
        text: result.data.length > 0
          ? `Found ${result.data.length} users with balance discrepancies (deleted shares)`
          : 'All users have correct share balances ✅'
      })
    } catch (error: any) {
      console.error('Error running balance audit:', error)
      setMessage({ type: 'error', text: `Audit failed: ${error.message}` })
    } finally {
      setLoading(false)
    }
  }

  // ============================================================================
  // TAB 2: AUDIT MISSING PAYMENTS (finds specific payments without share purchases)
  // ============================================================================

  const auditMissingPayments = async () => {
    setLoading(true)
    setMessage(null)
    setMissingShares([])

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'audit_missing_payments', data: {} })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setMissingShares(result.data)
      setSharesAuditRun(true)
      setMessage({
        type: result.data.length > 0 ? 'error' : 'success',
        text: result.data.length > 0
          ? `Found ${result.data.length} payments without share purchase records`
          : 'All payments have share purchase records ✅'
      })
    } catch (error: any) {
      console.error('Error running payments audit:', error)
      setMessage({ type: 'error', text: `Audit failed: ${error.message}` })
    } finally {
      setLoading(false)
    }
  }

  // ============================================================================
  // TAB 2: AUDIT MISSING COMMISSIONS
  // ============================================================================

  const auditMissingCommissions = async () => {
    setLoading(true)
    setMessage(null)
    setMissingCommissions([])

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'audit_missing_commissions', data: {} })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setMissingCommissions(result.data)
      setCommissionsAuditRun(true)
      setMessage({
        type: result.data.length > 0 ? 'error' : 'success',
        text: result.data.length > 0
          ? `Found ${result.data.length} share purchases with missing commissions`
          : 'All share purchases have commission records ✅'
      })
    } catch (error: any) {
      console.error('Error running commissions audit:', error)
      setMessage({ type: 'error', text: `Audit failed: ${error.message}` })
    } finally {
      setLoading(false)
    }
  }

  // ============================================================================
  // TAB 3: AUDIT MISSING BONUSES
  // ============================================================================

  const auditMissingBonuses = async () => {
    setLoading(true)
    setMessage(null)
    setMissingBonuses([])

    try {
      console.log('🔍 [AUDIT TAB 3] Starting Missing Bonuses Audit...')

      // TODO: Implement bonus calculation logic
      // This will check if bonus_shares field matches expected bonus based on amount

      setMessage({ type: 'success', text: 'Bonus audit not yet implemented' })
      setBonusesAuditRun(true)

    } catch (error: any) {
      console.error('Error running bonuses audit:', error)
      setMessage({ type: 'error', text: `Audit failed: ${error.message}` })
    } finally {
      setLoading(false)
    }
  }

  // ============================================================================
  // FIX FUNCTIONS
  // ============================================================================

  const fixMissingShares = async (issue: MissingShareIssue) => {
    setFixing(issue.payment_id)
    setMessage(null)

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'fix_missing_shares', data: { issue } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      if (result.emailsDeleted > 0) {
        setMessage({
          type: 'success',
          text: `✅ Created share purchase for ${issue.username} (${issue.expected_shares} shares) - NO EMAIL SENT`
        })
      } else {
        setMessage({
          type: 'error',
          text: `⚠️ Share purchase created but email notification may have been sent! Check user's email.`
        })
      }

      // Remove this issue from the list
      setMissingShares(prev => prev.filter(i => i.payment_id !== issue.payment_id))
    } catch (error: any) {
      console.error('Error fixing missing shares:', error)
      setMessage({ type: 'error', text: `Fix failed: ${error.message}` })
    } finally {
      setFixing(null)
    }
  }

  const fixMissingCommission = async (issue: MissingCommissionIssue) => {
    setFixing(issue.share_purchase_id)
    setMessage(null)

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'fix_missing_commission', data: { issue } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setMessage({
        type: 'success',
        text: `✅ Created commission for ${issue.sponsor_username}: $${issue.expected_usdt_commission.toFixed(2)} USDT + ${issue.expected_share_commission.toFixed(2)} shares - NO EMAIL SENT`
      })

      // Remove this issue from the list
      setMissingCommissions(prev => prev.filter(i => i.share_purchase_id !== issue.share_purchase_id))
    } catch (error: any) {
      console.error('Error fixing missing commission:', error)
      setMessage({ type: 'error', text: `Fix failed: ${error.message}` })
    } finally {
      setFixing(null)
    }
  }

  // ============================================================================
  // TAB 4: AUDIT DUPLICATE SHARES
  // ============================================================================

  const auditDuplicateShares = async () => {
    setLoading(true)
    setMessage(null)
    setDuplicateShares([])

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'audit_duplicate_shares', data: {} })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setDuplicateShares(result.data)
      setDuplicatesAuditRun(true)
    } catch (error: any) {
      console.error('Error auditing duplicate shares:', error)
      setMessage({ type: 'error', text: `Audit failed: ${error.message}` })
    } finally {
      setLoading(false)
    }
  }

  // ============================================================================
  // TAB 4: REVIEW DUPLICATE SHARES
  // ============================================================================

  const reviewDuplicateShares = async (issue: DuplicateShareIssue) => {
    setReviewingUser(issue)
    setMessage(null)

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'review_duplicate_shares', data: { issue } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setUserSharePurchases(result.purchases || [])
      setUserPayments(result.payments || [])
    } catch (error: any) {
      console.error('Error reviewing duplicate shares:', error)
      setMessage({ type: 'error', text: `Review failed: ${error.message}` })
    }
  }

  const deleteDuplicateSharePurchase = async (purchaseId: string) => {
    if (!reviewingUser) return

    const confirmed = window.confirm(
      `⚠️ DELETE SHARE PURCHASE?\n\n` +
      `This will permanently delete this share purchase record.\n\n` +
      `User: ${reviewingUser.username}\n` +
      `Purchase ID: ${purchaseId}\n\n` +
      `Are you sure?`
    )

    if (!confirmed) return

    setFixing(purchaseId)
    setMessage(null)

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'delete_duplicate_share_purchase', data: { purchaseId } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setMessage({ type: 'success', text: `Share purchase deleted successfully!` })

      // Refresh the user's share purchases
      await reviewDuplicateShares(reviewingUser)

      // Remove from duplicates list if no longer has excess shares
      const updatedPurchases = userSharePurchases.filter(p => p.id !== purchaseId)
      const newActualShares = updatedPurchases.reduce((sum, p) => sum + p.shares_purchased, 0)

      if (newActualShares <= reviewingUser.expected_shares) {
        setDuplicateShares(prev => prev.filter(d => d.user_id !== reviewingUser.user_id))
        setReviewingUser(null)
        setUserSharePurchases([])
      }
    } catch (error: any) {
      console.error('Error deleting share purchase:', error)
      setMessage({ type: 'error', text: `Delete failed: ${error.message}` })
    } finally {
      setFixing(null)
    }
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="payment-integrity-audit">
      <h2>💎 Payment Integrity Audit System</h2>
      <p className="subtitle">Sequential audit system - fix each tab in order</p>

      {/* Tab Navigation */}
      <div className="audit-tabs">
        <button
          className={`audit-tab ${activeTab === 'balance' ? 'active' : ''}`}
          onClick={() => setActiveTab('balance')}
        >
          <span className="tab-number">1</span>
          Balance Check
          {balanceAuditRun && (
            <span className={`tab-badge ${balanceIssues.length > 0 ? 'error' : 'success'}`}>
              {balanceIssues.length}
            </span>
          )}
        </button>

        <button
          className={`audit-tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          <span className="tab-number">2</span>
          Missing Payments
          {sharesAuditRun && (
            <span className={`tab-badge ${missingShares.length > 0 ? 'error' : 'success'}`}>
              {missingShares.length}
            </span>
          )}
        </button>

        <button
          className={`audit-tab ${activeTab === 'commissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('commissions')}
        >
          <span className="tab-number">3</span>
          Missing Commissions
          {commissionsAuditRun && (
            <span className={`tab-badge ${missingCommissions.length > 0 ? 'error' : 'success'}`}>
              {missingCommissions.length}
            </span>
          )}
        </button>

        <button
          className={`audit-tab ${activeTab === 'bonuses' ? 'active' : ''}`}
          onClick={() => setActiveTab('bonuses')}
        >
          <span className="tab-number">4</span>
          Missing Bonuses
          {bonusesAuditRun && (
            <span className={`tab-badge ${missingBonuses.length > 0 ? 'error' : 'success'}`}>
              {missingBonuses.length}
            </span>
          )}
        </button>

        <button
          className={`audit-tab ${activeTab === 'duplicates' ? 'active' : ''}`}
          onClick={() => setActiveTab('duplicates')}
        >
          <span className="tab-number">5</span>
          Duplicate Shares
          {duplicatesAuditRun && (
            <span className={`tab-badge ${duplicateShares.length > 0 ? 'error' : 'success'}`}>
              {duplicateShares.length}
            </span>
          )}
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`audit-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tab 1: Balance Check */}
      {activeTab === 'balance' && (
        <div className="audit-tab-content">
          <div className="audit-header">
            <h3>⚖️ Tab 1: Balance Discrepancy Check</h3>
            <p>Find users whose total share balance doesn't match their total payments (DELETED shares)</p>
            <button
              onClick={auditBalanceDiscrepancies}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Running Audit...' : 'Run Balance Audit'}
            </button>
          </div>

          {balanceAuditRun && balanceIssues.length === 0 && (
            <div className="audit-success">
              ✅ All users have correct share balances!
            </div>
          )}

          {balanceIssues.length > 0 && (
            <div className="audit-results">
              <h4>🚨 Found {balanceIssues.length} Users with Balance Discrepancies</h4>
              <p style={{ color: '#ff6b6b', marginBottom: '1rem' }}>
                ⚠️ These users have LESS shares than they should based on their payments (shares were DELETED!)
              </p>
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Amount</th>
                    <th>Expected Shares</th>
                    <th>Payment Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {balanceIssues.map((issue) => (
                    <tr key={issue.payment_id}>
                      <td>{issue.username}</td>
                      <td>{issue.email}</td>
                      <td>${issue.amount.toFixed(2)}</td>
                      <td>{issue.expected_shares} shares</td>
                      <td>{new Date(issue.payment_date).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => fixMissingShares(issue)}
                          disabled={fixing === issue.payment_id}
                          className="btn-fix"
                        >
                          {fixing === issue.payment_id ? 'Fixing...' : 'Fix'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Missing Payments */}
      {activeTab === 'payments' && (
        <div className="audit-tab-content">
          <div className="audit-header">
            <h3>📊 Tab 2: Missing Share Purchase Records</h3>
            <p>Find specific approved payments that don't have share purchase records</p>
            <button
              onClick={auditMissingPayments}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Running Audit...' : 'Run Payments Audit'}
            </button>
          </div>

          {sharesAuditRun && missingShares.length === 0 && (
            <div className="audit-success">
              ✅ All approved payments have share purchase records!
            </div>
          )}

          {missingShares.length > 0 && (
            <div className="audit-results">
              <h4>🚨 Found {missingShares.length} Payments Without Share Purchase Records</h4>
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Amount</th>
                    <th>Expected Shares</th>
                    <th>Payment Date</th>
                    <th>Network</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {missingShares.map((issue) => (
                    <tr key={issue.payment_id}>
                      <td>{issue.username}</td>
                      <td>{issue.email}</td>
                      <td>${issue.amount.toFixed(2)}</td>
                      <td>{issue.expected_shares} shares</td>
                      <td>{new Date(issue.payment_date).toLocaleDateString()}</td>
                      <td>{issue.network}</td>
                      <td>
                        <button
                          onClick={() => fixMissingShares(issue)}
                          disabled={fixing === issue.payment_id}
                          className="btn-fix"
                        >
                          {fixing === issue.payment_id ? 'Fixing...' : 'Fix'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Missing Commissions */}
      {activeTab === 'commissions' && (
        <div className="audit-tab-content">
          <div className="audit-header">
            <h3>💰 Tab 3: Missing Commission Records</h3>
            <p>Find share purchases that don't have commission records (run after Tabs 1 & 2 are clean)</p>
            <button
              onClick={auditMissingCommissions}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Running Audit...' : 'Run Commissions Audit'}
            </button>
          </div>

          {commissionsAuditRun && missingCommissions.length === 0 && (
            <div className="audit-success">
              ✅ All share purchases have commission records!
            </div>
          )}

          {missingCommissions.length > 0 && (
            <div className="audit-results">
              <h4>🚨 Found {missingCommissions.length} Missing Commission Records</h4>
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Sponsor</th>
                    <th>Sponsor Current Balance</th>
                    <th>Amount</th>
                    <th>Shares</th>
                    <th>Expected Commission</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {missingCommissions.map((issue) => (
                    <tr key={issue.share_purchase_id}>
                      <td>{issue.username}</td>
                      <td>
                        {issue.sponsor_username}<br/>
                        <small style={{ color: '#888' }}>
                          ({issue.sponsor_total_commissions || 0} commissions)
                        </small>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.9em' }}>
                          💵 ${(issue.sponsor_current_usdt || 0).toFixed(2)} USDT<br/>
                          📊 {(issue.sponsor_current_shares || 0).toFixed(2)} shares
                        </div>
                      </td>
                      <td>${issue.amount.toFixed(2)}</td>
                      <td>{issue.shares_purchased} shares</td>
                      <td>
                        ${issue.expected_usdt_commission.toFixed(2)} USDT<br/>
                        + {issue.expected_share_commission.toFixed(2)} shares
                      </td>
                      <td>
                        <button
                          onClick={() => fixMissingCommission(issue)}
                          disabled={fixing === issue.share_purchase_id}
                          className="btn-fix"
                        >
                          {fixing === issue.share_purchase_id ? 'Fixing...' : 'Fix'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Missing Bonuses */}
      {activeTab === 'bonuses' && (
        <div className="audit-tab-content">
          <div className="audit-header">
            <h3>🎁 Tab 4: Missing Bonus Shares</h3>
            <p>Find share purchases with incorrect bonus shares (run after Tabs 1, 2 & 3 are clean)</p>
            <button
              onClick={auditMissingBonuses}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Running Audit...' : 'Run Bonuses Audit'}
            </button>
          </div>

          {bonusesAuditRun && (
            <div className="audit-info">
              ℹ️ Bonus audit not yet implemented
            </div>
          )}
        </div>
      )}

      {/* Tab 5: Duplicate Shares */}
      {activeTab === 'duplicates' && (
        <div className="audit-tab-content">
          <div className="audit-header">
            <h3>🔄 Tab 5: Duplicate/Excess Shares</h3>
            <p>Find users who have MORE shares than their payments should give them (run LAST to catch fix errors)</p>
            <button
              onClick={auditDuplicateShares}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Running Audit...' : 'Run Duplicates Audit'}
            </button>
          </div>

          {duplicatesAuditRun && duplicateShares.length === 0 && (
            <div className="audit-success">
              ✅ No duplicate/excess shares found! All users have correct share amounts.
            </div>
          )}

          {duplicatesAuditRun && duplicateShares.length > 0 && (
            <div className="audit-results">
              <div className="audit-error">
                🚨 Found {duplicateShares.length} Users with Excess Shares
              </div>

              <table className="audit-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Payments</th>
                    <th>Total Paid</th>
                    <th>Expected Shares</th>
                    <th>Actual Shares</th>
                    <th>Excess Shares</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateShares.map((issue) => (
                    <tr key={issue.user_id}>
                      <td>{issue.username}</td>
                      <td>{issue.email}</td>
                      <td>{issue.total_payments}</td>
                      <td>${issue.total_payment_amount.toFixed(2)}</td>
                      <td>{issue.expected_shares} shares</td>
                      <td>{issue.actual_shares} shares</td>
                      <td style={{ color: '#ff4444', fontWeight: 'bold' }}>
                        +{issue.excess_shares} shares
                      </td>
                      <td>
                        <button
                          className="btn-warning"
                          onClick={() => reviewDuplicateShares(issue)}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Review Modal */}
      {reviewingUser && (
        <div className="modal-overlay" onClick={() => setReviewingUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔍 Review Duplicate Shares: {reviewingUser.username}</h3>
              <button className="modal-close" onClick={() => setReviewingUser(null)}>×</button>
            </div>

            <div className="modal-body">
              <div className="review-summary">
                <p><strong>Email:</strong> {reviewingUser.email}</p>
                <p><strong>Total Paid:</strong> ${reviewingUser.total_payment_amount.toFixed(2)}</p>
                <p><strong>Expected Shares:</strong> {reviewingUser.expected_shares} shares</p>
                <p><strong>Actual Shares:</strong> {reviewingUser.actual_shares} shares</p>
                <p style={{ color: '#ff4444', fontWeight: 'bold' }}>
                  <strong>Excess Shares:</strong> +{reviewingUser.excess_shares} shares
                </p>
              </div>

              <h4>💰 Approved Payments ({userPayments.length}):</h4>

              {userPayments.length === 0 ? (
                <p>Loading payments...</p>
              ) : (
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Payment ID</th>
                      <th>Approved Date</th>
                      <th>Amount</th>
                      <th>Network</th>
                      <th>Proof / Hash / Reference</th>
                      <th>Shares Expected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPayments.map((payment) => {
                      const isBankTransfer = payment.network === 'BANK_TRANSFER'
                      // Issue #22 Phase 2b: only screenshot_url is a viewable proof object;
                      // sender_wallet is a wallet/account string and must never be treated as a URL.
                      const proofUrl = payment.screenshot_url || null

                      // Parse transaction notes for bank transfer reference
                      let referenceNumber = null
                      if (payment.transaction_notes) {
                        try {
                          const notes = JSON.parse(payment.transaction_notes)
                          referenceNumber = notes.reference_number
                        } catch (e) {
                          // Ignore parse errors
                        }
                      }

                      return (
                        <tr key={payment.id}>
                          <td style={{ fontSize: '13px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {payment.id}
                          </td>
                          <td>{new Date(payment.approved_at).toLocaleString()}</td>
                          <td>${parseFloat(payment.amount).toFixed(2)}</td>
                          <td>{payment.network}</td>
                          <td>
                            {isBankTransfer ? (
                              <div>
                                {proofUrl && (
                                  <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); openSignedProof(proofUrl); }}
                                    style={{ color: '#d4af37', textDecoration: 'underline', display: 'block', marginBottom: '4px' }}
                                  >
                                    📄 View Proof
                                  </a>
                                )}
                                {referenceNumber && (
                                  <div style={{ fontSize: '13px', color: '#999' }}>
                                    Ref: {referenceNumber}
                                  </div>
                                )}
                              </div>
                            ) : (
                              payment.transaction_hash ? (
                                <a
                                  href={`https://tronscan.org/#/transaction/${payment.transaction_hash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: '13px', color: '#d4af37', textDecoration: 'underline', maxWidth: '150px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  title={payment.transaction_hash}
                                >
                                  {payment.transaction_hash}
                                </a>
                              ) : (
                                <span style={{ color: '#999' }}>N/A</span>
                              )
                            )}
                          </td>
                          <td>{payment.shares_to_purchase} shares</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}

              <h4 style={{ marginTop: '30px' }}>📦 Share Purchase Records ({userSharePurchases.length}):</h4>

              {userSharePurchases.length === 0 ? (
                <p>Loading share purchases...</p>
              ) : (
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Purchase ID</th>
                      <th>Created Date</th>
                      <th>Shares</th>
                      <th>Amount</th>
                      <th>Linked Payment</th>
                      <th>Proof / Hash / Ref</th>
                      <th>Match?</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userSharePurchases.map((purchase) => {
                      // Check if this purchase matches a payment
                      const matchedPayment = userPayments.find(p =>
                        p.id === purchase.transaction_reference ||
                        p.transaction_hash === purchase.transaction_reference
                      )

                      // Parse transaction notes for bank transfer reference
                      let referenceNumber = null
                      if (matchedPayment?.transaction_notes) {
                        try {
                          const notes = JSON.parse(matchedPayment.transaction_notes)
                          referenceNumber = notes.reference_number
                        } catch (e) {
                          // Ignore parse errors
                        }
                      }

                      const isBankTransfer = matchedPayment?.network === 'BANK_TRANSFER'
                      // Issue #22 Phase 2b: only screenshot_url is a viewable proof object;
                      // sender_wallet is a wallet/account string and must never be treated as a URL.
                      const proofUrl = matchedPayment?.screenshot_url || null

                      return (
                        <tr key={purchase.id}>
                          <td style={{ fontSize: '13px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {purchase.id}
                          </td>
                          <td>{new Date(purchase.created_at).toLocaleString()}</td>
                          <td>{purchase.shares_purchased} shares</td>
                          <td>${parseFloat(purchase.total_amount).toFixed(2)}</td>
                          <td style={{ fontSize: '13px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {purchase.transaction_reference || 'N/A'}
                          </td>
                          <td>
                            {matchedPayment ? (
                              isBankTransfer ? (
                                <div>
                                  {proofUrl && (
                                    <a
                                      href="#"
                                      onClick={(e) => { e.preventDefault(); openSignedProof(proofUrl); }}
                                      style={{ color: '#d4af37', textDecoration: 'underline', display: 'block', marginBottom: '4px' }}
                                    >
                                      📄 Proof
                                    </a>
                                  )}
                                  {referenceNumber && (
                                    <div style={{ fontSize: '13px', color: '#999' }}>
                                      {referenceNumber}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                matchedPayment.transaction_hash ? (
                                  <a
                                    href={`https://tronscan.org/#/transaction/${matchedPayment.transaction_hash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '13px', color: '#d4af37', textDecoration: 'underline', maxWidth: '120px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    title={matchedPayment.transaction_hash}
                                  >
                                    {matchedPayment.transaction_hash.substring(0, 12)}...
                                  </a>
                                ) : (
                                  <span style={{ color: '#999' }}>N/A</span>
                                )
                              )
                            ) : (
                              <span style={{ color: '#999' }}>-</span>
                            )}
                          </td>
                          <td>
                            {matchedPayment ? (
                              <span style={{ color: '#00ff00' }}>✅ Yes</span>
                            ) : (
                              <span style={{ color: '#ff4444' }}>❌ No</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn-danger"
                              onClick={() => deleteDuplicateSharePurchase(purchase.id)}
                              disabled={fixing === purchase.id}
                            >
                              {fixing === purchase.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}

              <div className="modal-info">
                ℹ️ <strong>Instructions:</strong> Compare the payments (top table) with share purchases (bottom table).
                <br/>• If 1 payment has 2+ share purchases linked to it → Delete the duplicate share purchase
                <br/>• If share purchase shows "❌ No" match → It's orphaned and should be deleted
                <br/>• Usually the OLDER duplicate record should be deleted
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .payment-integrity-audit {
          padding: 20px;
          background: #0a0a0a;
          min-height: 100vh;
          color: #e0e0e0;
        }

        .payment-integrity-audit h2 {
          color: #d4af37;
          margin-bottom: 10px;
        }

        .subtitle {
          color: #999;
          margin-bottom: 20px;
        }

        .critical-warning {
          background: #ff4444;
          color: white;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
          border: 3px solid #ff0000;
        }

        .critical-warning h3 {
          margin: 0 0 15px 0;
          color: white;
          font-size: 20px;
        }

        .critical-warning p {
          margin: 10px 0;
          color: white;
        }

        .critical-warning ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .critical-warning li {
          margin: 5px 0;
          color: white;
        }

        .critical-warning strong {
          color: #ffff00;
        }

        .audit-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
        }

        .audit-tab {
          padding: 15px 25px;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
          color: #999;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s;
        }

        .audit-tab:hover {
          color: #d4af37;
          background: #1a1a1a;
        }

        .audit-tab.active {
          color: #d4af37;
          border-bottom-color: #d4af37;
        }

        .tab-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #333;
          color: #999;
          font-size: 14px;
          font-weight: bold;
        }

        .audit-tab.active .tab-number {
          background: #d4af37;
          color: #0a0a0a;
        }

        .tab-badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
        }

        .tab-badge.error {
          background: #ff4444;
          color: white;
        }

        .tab-badge.success {
          background: #00C851;
          color: white;
        }

        .audit-message {
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .audit-message.success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .audit-message.error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .audit-header {
          margin-bottom: 30px;
        }

        .audit-header h3 {
          margin-bottom: 10px;
          color: #d4af37;
        }

        .audit-header p {
          color: #999;
          margin-bottom: 20px;
        }

        .audit-tab-content {
          background: #0a0a0a;
        }

        .btn-primary {
          padding: 12px 24px;
          background: #d4af37;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-primary:hover:not(:disabled) {
          background: #b8941f;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .audit-success,
        .audit-info {
          padding: 20px;
          border-radius: 8px;
          text-align: center;
          font-size: 18px;
        }

        .audit-success {
          background: #d4edda;
          color: #155724;
        }

        .audit-info {
          background: #d1ecf1;
          color: #0c5460;
        }

        .audit-results {
          margin-top: 20px;
        }

        .audit-results h4 {
          margin-bottom: 20px;
          color: #ff6b6b;
        }

        .audit-table {
          width: 100%;
          border-collapse: collapse;
          background: #1a1a1a;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          border: 1px solid #333;
        }

        .audit-table th {
          background: #2a2a2a;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid #444;
          color: #d4af37;
        }

        .audit-table td {
          padding: 12px;
          border-bottom: 1px solid #333;
          color: #e0e0e0;
        }

        .audit-table tr:last-child td {
          border-bottom: none;
        }

        .audit-table tr:hover {
          background: #252525;
        }

        .btn-fix {
          padding: 8px 16px;
          background: #00C851;
          color: white;
          border: none;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-fix:hover:not(:disabled) {
          background: #007E33;
        }

        .btn-fix:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-danger {
          padding: 8px 16px;
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-danger:hover:not(:disabled) {
          background: #c82333;
        }

        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .modal-content {
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 8px;
          max-width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          padding: 0;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #333;
        }

        .modal-header h3 {
          margin: 0;
          color: #d4af37;
        }

        .modal-close {
          background: none;
          border: none;
          color: #999;
          font-size: 32px;
          cursor: pointer;
          padding: 0;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close:hover {
          color: #fff;
        }

        .modal-body {
          padding: 20px;
        }

        .review-summary {
          background: #0a0a0a;
          padding: 15px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .review-summary p {
          margin: 8px 0;
          color: #ccc;
        }

        .modal-info {
          background: #1e3a5f;
          padding: 15px;
          border-radius: 6px;
          margin-top: 20px;
          color: #ccc;
        }
      `}</style>
    </div>
  )
}
